import { LinkListGroupElementElementModel, TrustedMemberPageContentResponseModel } from '@shared/integrations/umbraco-taf-delivery-api/models';
import Alerts from '@shared/components/Alerts.tsx';
import AlertBox from '@shared/components/AlertBox.tsx';
import LinkListGroup from '@shared/components/LinkListGroup.tsx';
import Loader from '@shared/components/Loader.tsx';
import MemberTable from '../../../components/MemberTable';
import { useClubMembers } from '@my-pages/features/trusted-member/hooks/useClubMembers.ts';
import Feature from '@shared/features/feature-flags/components/Feature.tsx';
import { FeatureFlag } from '@shared/_legacy/api/umbraco-api';
import useApplicationStore from '@my-pages/stores/useApplicationStore.tsx';

interface ITrustedMemberPageProps {
    content: TrustedMemberPageContentResponseModel;
}

const TrustedMemberPage = ({ content }: ITrustedMemberPageProps) => {
    const { user } = useApplicationStore();
    const canSeeClubMembers =
        user?.roles?.includes('Kassör klubbstyrelse Klubb 6-6-5') ||
        user?.roles?.includes('Sekreterare klubbstyrelse Klubb 6-8-5') ||
        user?.roles?.includes('Styrelseordförande Klubb 6-2-5') ||
        user?.roles?.includes('DevUser');

    const { data: members, isLoading, isError, error } = useClubMembers(canSeeClubMembers);

    const renderClubMembersSection = () => {
        if (isLoading) {
            return <Loader />;
        }

        if (isError && error) {
            const errorMessage = error.response?.data?.detail || error.message || 'Ett oväntat fel uppstod vid hämtning av klubbmedlemmar.';

            return (
                <AlertBox
                    theme='danger'
                    heading='Kunde inte hämta klubbmedlemmar'
                >
                    <p>{errorMessage}</p>
                    <p className='mb-0'>
                        Vid frågor kontakta oss på{' '}
                        <a
                            href='tel:010-480 30 00'
                            className='text-nowrap'
                        >
                            010-480 30 00
                        </a>
                    </p>
                </AlertBox>
            );
        }

        if (members && members.length > 0) {
            return <MemberTable members={members} />;
        }

        return (
            <AlertBox
                theme='info'
                heading='Inga medlemmar hittades'
            >
                <p className='mb-0'>Det finns inga klubbmedlemmar att visa för tillfället.</p>
            </AlertBox>
        );
    };

    return (
        <>
            <h1 className='page-title'>Förtroendevald</h1>

            <div className='slide-up-fade-in'>
                {content?.properties?.importantMessages && (
                    <div className='mb-4'>
                        <Alerts alerts={content?.properties?.importantMessages} />
                    </div>
                )}
                <div className='card card-body mb-4'>
                    <div className='row'>
                        <div className='col-12'>
                            {content?.properties?.linkListGroups?.items && (
                                <div>
                                    {content?.properties?.linkListGroups?.items?.map((item, key) => {
                                        if (!item.content) return null;
                                        const element = item.content as LinkListGroupElementElementModel;
                                        return (
                                            <LinkListGroup
                                                key={key}
                                                element={element}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                    {canSeeClubMembers && (
                        <Feature feature={FeatureFlag.SHOW_TRUSTED_MEMBER_MEMBERS_LIST}>
                            <div>
                                <h3 className='mb-3'>Klubbmedlemmar</h3>
                                {renderClubMembersSection()}
                            </div>
                        </Feature>
                    )}
                </div>
            </div>
        </>
    );
};

export default TrustedMemberPage;
